import React, { useEffect, useMemo, useState } from 'react';
import {
  FaArrowRight,
  FaCheckCircle,
  FaCopy,
  FaCogs,
  FaExclamationTriangle,
  FaKey,
  FaLink,
  FaPlus,
  FaPlug,
  FaRobot,
  FaSearchDollar,
  FaSync,
  FaTrash,
  FaUsers,
  FaBoxes,
  FaBuilding,
} from 'react-icons/fa';
import api from '../api';
import ProcessIntegrationSettings from './ProcessIntegrationSettings';
import InfoTooltip from '../components/InfoTooltip';

const formatDateTime = (iso) => {
  if (!iso) {
    return 'Nunca executado';
  }
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return 'Nunca executado';
    }
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(date);
  } catch (error) {
    return 'Nunca executado';
  }
};

const automationTemplates = [
  {
    id: 'http-webhook',
    title: 'Webhook HTTP (genérico)',
    shortLabel: 'Webhook REST',
    description: 'Envie dados estruturados para qualquer endpoint REST compatível com JSON.',
    icon: FaPlug,
    accent: 'bg-blue-100 text-blue-700',
    template: {
      nameSuffix: 'Webhook REST',
      targetUrl: 'https://api.seusistema.com/webhook',
      method: 'POST',
      events: ['PROCESS_COMPLETED'],
      headers: { 'Content-Type': 'application/json' },
      timeoutMs: 15000,
    },
  },
  {
    id: 'uipath',
    title: 'UiPath Orchestrator',
    shortLabel: 'UiPath',
    description: 'Dispare jobs do UiPath com correlação automática de instâncias.',
    icon: FaRobot,
    accent: 'bg-amber-100 text-amber-700',
    template: {
      nameSuffix: 'UiPath Orchestrator',
      targetUrl: 'https://orchestrator.seudominio.com/odata/Jobs/UiPath.Server.Configuration.OData.StartJobs',
      method: 'POST',
      events: ['STEP_COMPLETED'],
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer {{token}}',
      },
      timeoutMs: 20000,
    },
  },
  {
    id: 'price-lookup-rpa',
    title: 'Robô RPA – Consulta de preços',
    shortLabel: 'Busca de preços',
    description:
      'Acione o robô padrão que pesquisa valores em provedores online a partir de um campo do formulário.',
    icon: FaSearchDollar,
    accent: 'bg-emerald-100 text-emerald-700',
    template: {
      nameSuffix: 'Robô de preços RPA',
      targetUrl: 'https://rpa.seudominio.com/robots/price-lookup/run',
      method: 'POST',
      events: ['STEP_COMPLETED'],
      headers: { 'Content-Type': 'application/json' },
      timeoutMs: 30000,
    },
  },
  {
    id: 'power-automate',
    title: 'Power Automate (Flow)',
    shortLabel: 'Power Automate',
    description: 'Conecte um fluxo do Power Automate e receba a resposta em tempo real.',
    icon: FaCogs,
    accent: 'bg-purple-100 text-purple-700',
    template: {
      nameSuffix: 'Power Automate Flow',
      targetUrl: 'https://prod-00.westeurope.logic.azure.com:443/...',
      method: 'POST',
      events: ['PROCESS_COMPLETED'],
      headers: { 'Content-Type': 'application/json' },
      timeoutMs: 20000,
    },
  },
];

const automationHighlights = [
  {
    id: 'templates',
    label: 'Templates prontos',
    description:
      'Selecione o processo e aplique um template pré-configurado como RPA de preços, UiPath, Power Automate ou Webhook.',
  },
  {
    id: 'monitoring',
    label: 'Indicadores em tempo real',
    description: 'Acompanhe tentativas, erros e tempo de resposta das integrações ativas.',
  },
  {
    id: 'fallback',
    label: 'Fallback orientado',
    description: 'Informe quando o time operacional deve assumir a execução manual.',
  },
];

const automationTips = [
  {
    id: 'payload',
    label: 'Padronize o payload',
    description:
      'Valide entradas e saídas com os schemas publicados nas integrações para evitar erros em produção.',
  },
  {
    id: 'monitoring',
    label: 'Monitore execuções',
    description:
      'Habilite logs, métricas de tempo e alertas antes de liberar a automação para os usuários finais.',
  },
  {
    id: 'fallback',
    label: 'Defina fallback',
    description:
      'Planeje quando redirecionar para execução manual e como retomar a automação com segurança.',
  },
];

function UserProvisioningAutomationPanel() {
  const [sources, setSources] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    sourceKey: '',
    token: '',
    passwordPattern: 'CPF6',
    fixedPassword: '',
    defaultGroupId: '',
    defaultSectorId: '',
    loginUrl: '',
    sendAccessEmail: true,
  });

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [sourcesResponse, groupsResponse, sectorsResponse] = await Promise.all([
        api.get('/users/provisioning/sources'),
        api.get('/groups'),
        api.get('/sectors'),
      ]);
      setSources(Array.isArray(sourcesResponse.data) ? sourcesResponse.data : []);
      setGroups(Array.isArray(groupsResponse.data) ? groupsResponse.data : []);
      setSectors(Array.isArray(sectorsResponse.data) ? sectorsResponse.data : []);
    } catch (err) {
      const message = err?.response?.data?.error || 'Não foi possível carregar a automação de cadastro de usuários.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const clearForm = () => {
    setForm({
      name: '',
      sourceKey: '',
      token: '',
      passwordPattern: 'CPF6',
      fixedPassword: '',
      defaultGroupId: '',
      defaultSectorId: '',
      loginUrl: '',
      sendAccessEmail: true,
    });
  };

  const handleCreateSource = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setFeedback('');
    try {
      const payload = {
        name: form.name,
        sourceKey: form.sourceKey || form.name,
        token: form.token || undefined,
        passwordPattern: form.passwordPattern,
        fixedPassword: form.passwordPattern === 'FIXED' ? form.fixedPassword : undefined,
        defaultGroupId: form.defaultGroupId ? Number(form.defaultGroupId) : undefined,
        defaultSectorId: form.defaultSectorId ? Number(form.defaultSectorId) : undefined,
        loginUrl: form.loginUrl || undefined,
        sendAccessEmail: Boolean(form.sendAccessEmail),
      };
      const { data } = await api.post('/users/provisioning/sources', payload);
      setFeedback(`Fonte criada com sucesso. Token gerado: ${data?.token || '(não informado)'}`);
      clearForm();
      await loadData();
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao criar fonte de integração';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSource = async (sourceId) => {
    const confirmed = window.confirm('Deseja remover esta fonte de integração?');
    if (!confirmed) return;
    setError('');
    setFeedback('');
    try {
      await api.delete(`/users/provisioning/sources/${sourceId}`);
      setFeedback('Fonte removida com sucesso.');
      await loadData();
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao remover fonte';
      setError(message);
    }
  };

  const handleToggleActive = async (source) => {
    setError('');
    setFeedback('');
    try {
      await api.put(`/users/provisioning/sources/${source.id}`, {
        active: !source.active,
      });
      setFeedback(`Fonte ${!source.active ? 'ativada' : 'desativada'} com sucesso.`);
      await loadData();
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao atualizar status da fonte';
      setError(message);
    }
  };

  const copyToClipboard = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setFeedback(`${label} copiado com sucesso.`);
    } catch (err) {
      setError(`Não foi possível copiar ${label.toLowerCase()}.`);
    }
  };

  const resolveIntakeUrl = (source) => {
    const base = String(api?.defaults?.baseURL || window.location.origin).replace(/\/$/, '');
    const path = source?.intakePath || `/users/provisioning/intake/${source?.sourceKey || ''}`;
    return `${base}${path}`;
  };

  const payloadExample = useMemo(() => {
    const selectedGroupId = form.defaultGroupId ? Number(form.defaultGroupId) : null;
    const selectedSectorId = form.defaultSectorId ? Number(form.defaultSectorId) : null;

    const examplePayload = {
      users: [
        {
          name: 'Nome da Pessoa',
          email: 'usuario@empresa.com',
          cpf: '12345678900',
          groupId: Number.isFinite(selectedGroupId) ? selectedGroupId : null,
          sectorId: Number.isFinite(selectedSectorId) ? selectedSectorId : null,
        },
      ],
    };

    return JSON.stringify(examplePayload, null, 2);
  }, [form.defaultGroupId, form.defaultSectorId]);

  return (
    <section className="space-y-5 rounded-3xl border border-slate-200/70 bg-white/95 p-6 shadow-xl">
      <header className="flex flex-col gap-2">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
          <FaUsers className="text-blue-600" /> Automação de Cadastro de Usuário
        </h2>
        <p className="text-sm text-slate-600">
          Configure fontes externas para o sistema gerar automaticamente a API de intake. Depois, basta colar o link no outro sistema e enviar os usuários no padrão abaixo.
        </p>
      </header>

      {feedback ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{feedback}</div>
      ) : null}
      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      <form onSubmit={handleCreateSource} className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Nome da fonte</label>
          <input value={form.name} onChange={(e) => setField('name', e.target.value)} required className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Ex.: ERP RH Matriz" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Chave da fonte</label>
          <input value={form.sourceKey} onChange={(e) => setField('sourceKey', e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Opcional (se vazio, usa o nome)" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Token</label>
          <input value={form.token} onChange={(e) => setField('token', e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Opcional (se vazio, será gerado)" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Padrão de senha</label>
          <select value={form.passwordPattern} onChange={(e) => setField('passwordPattern', e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="CPF6">6 primeiros dígitos do CPF</option>
            <option value="FIXED">Senha fixa</option>
          </select>
        </div>
        {form.passwordPattern === 'FIXED' ? (
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Senha fixa</label>
            <input value={form.fixedPassword} onChange={(e) => setField('fixedPassword', e.target.value)} required className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          </div>
        ) : null}
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Grupo padrão (opcional)</label>
          <select value={form.defaultGroupId} onChange={(e) => setField('defaultGroupId', e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="">Sem grupo padrão</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Setor padrão (opcional)</label>
          <select value={form.defaultSectorId} onChange={(e) => setField('defaultSectorId', e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="">Sem setor padrão</option>
            {sectors.map((sector) => (
              <option key={sector.id} value={sector.id}>{sector.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Link de login (opcional)</label>
          <input value={form.loginUrl} onChange={(e) => setField('loginUrl', e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="https://seu-dominio.com/login" />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700 md:col-span-2">
          <input type="checkbox" checked={form.sendAccessEmail} onChange={(e) => setField('sendAccessEmail', e.target.checked)} />
          Enviar e-mail automático com login/senha/link após cadastro
        </label>
        <div className="md:col-span-2">
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-70">
            <FaPlus /> {saving ? 'Salvando...' : 'Criar fonte e gerar API'}
          </button>
        </div>
      </form>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Payload padrão</h3>
        <pre className="overflow-auto rounded-xl bg-slate-900 p-3 text-xs text-slate-100">{payloadExample}</pre>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Fontes configuradas</h3>
        {loading ? (
          <div className="text-sm text-slate-500">Carregando fontes...</div>
        ) : sources.length ? (
          <div className="space-y-3">
            {sources.map((source) => {
              const intakeUrl = resolveIntakeUrl(source);
              return (
                <article key={source.id} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h4 className="text-base font-semibold text-slate-900">{source.name}</h4>
                      <p className="text-xs text-slate-500">sourceKey: {source.sourceKey}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button type="button" onClick={() => handleToggleActive(source)} className={`rounded-lg px-3 py-1 text-xs font-semibold ${source.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}`}>
                        {source.active ? 'Ativa' : 'Inativa'}
                      </button>
                      <button type="button" onClick={() => handleDeleteSource(source.id)} className="inline-flex items-center gap-1 rounded-lg bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
                        <FaTrash /> Remover
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-2 text-xs text-slate-700 md:grid-cols-2">
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="mb-1 font-semibold text-slate-500">API / Link de envio</p>
                      <p className="break-all">{intakeUrl}</p>
                      <button type="button" onClick={() => copyToClipboard(intakeUrl, 'Link de API')} className="mt-2 inline-flex items-center gap-1 text-blue-600">
                        <FaLink /> <FaCopy /> Copiar link
                      </button>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="mb-1 font-semibold text-slate-500">Token de autenticação</p>
                      <p className="break-all">{source.token || 'Sem token disponível'}</p>
                      {source.token ? (
                        <button type="button" onClick={() => copyToClipboard(source.token, 'Token')} className="mt-2 inline-flex items-center gap-1 text-blue-600">
                          <FaKey /> <FaCopy /> Copiar token
                        </button>
                      ) : null}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-500">
            Nenhuma fonte criada ainda. Cadastre uma fonte para gerar o endpoint de integração.
          </div>
        )}
      </div>
    </section>
  );
}

function ResourceProvisioningPanel({
  title,
  description,
  icon: Icon,
  sourceBasePath,
  defaultPath,
  payloadExample,
}) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', sourceKey: '', token: '' });

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`${sourceBasePath}/sources`);
      setSources(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Não foi possível carregar as fontes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [sourceBasePath]);

  const resolveIntakeUrl = (source) => {
    const base = String(api?.defaults?.baseURL || window.location.origin).replace(/\/$/, '');
    const path = source?.intakePath || `${defaultPath}/${source?.sourceKey || ''}`;
    return `${base}${path}`;
  };

  const copyToClipboard = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setFeedback(`${label} copiado com sucesso.`);
    } catch (err) {
      setError(`Não foi possível copiar ${label.toLowerCase()}.`);
    }
  };

  const handleCreateSource = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setFeedback('');
    try {
      const { data } = await api.post(`${sourceBasePath}/sources`, {
        name: form.name,
        sourceKey: form.sourceKey || form.name,
        token: form.token || undefined,
      });
      setFeedback(`Fonte criada com sucesso. Token gerado: ${data?.token || '(não informado)'}`);
      setForm({ name: '', sourceKey: '', token: '' });
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || 'Erro ao criar fonte de integração.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (source) => {
    try {
      await api.put(`${sourceBasePath}/sources/${source.id}`, { active: !source.active });
      setFeedback(`Fonte ${!source.active ? 'ativada' : 'desativada'} com sucesso.`);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || 'Erro ao atualizar a fonte.');
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!window.confirm('Deseja remover esta fonte de integração?')) return;
    try {
      await api.delete(`${sourceBasePath}/sources/${sourceId}`);
      setFeedback('Fonte removida com sucesso.');
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || 'Erro ao remover fonte.');
    }
  };

  return (
    <section className="space-y-5 rounded-3xl border border-slate-200/70 bg-white/95 p-6 shadow-xl">
      <header className="flex flex-col gap-2">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
          <Icon className="text-blue-600" /> {title}
        </h2>
        <p className="text-sm text-slate-600">{description}</p>
      </header>
      {feedback ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{feedback}</div> : null}
      {error ? <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}

      <form onSubmit={handleCreateSource} className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 md:grid-cols-3">
        <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} required className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Nome da fonte" />
        <input value={form.sourceKey} onChange={(e) => setForm((prev) => ({ ...prev, sourceKey: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Chave da fonte (opcional)" />
        <input value={form.token} onChange={(e) => setForm((prev) => ({ ...prev, token: e.target.value }))} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Token (opcional)" />
        <div className="md:col-span-3">
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-70">
            <FaPlus /> {saving ? 'Salvando...' : 'Criar fonte e gerar API'}
          </button>
        </div>
      </form>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Payload padrão</h3>
        <pre className="overflow-auto rounded-xl bg-slate-900 p-3 text-xs text-slate-100">{payloadExample}</pre>
      </div>

      {loading ? <div className="text-sm text-slate-500">Carregando fontes...</div> : (
        <div className="space-y-3">
          {sources.map((source) => {
            const intakeUrl = resolveIntakeUrl(source);
            return (
              <article key={source.id} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h4 className="text-base font-semibold text-slate-900">{source.name}</h4>
                    <p className="text-xs text-slate-500">sourceKey: {source.sourceKey}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" onClick={() => handleToggleActive(source)} className={`rounded-lg px-3 py-1 text-xs font-semibold ${source.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}`}>
                      {source.active ? 'Ativa' : 'Inativa'}
                    </button>
                    <button type="button" onClick={() => handleDeleteSource(source.id)} className="inline-flex items-center gap-1 rounded-lg bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
                      <FaTrash /> Remover
                    </button>
                  </div>
                </div>
                <div className="mt-3 grid gap-2 text-xs text-slate-700 md:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <p className="mb-1 font-semibold text-slate-500">API / Link de envio</p>
                    <p className="break-all">{intakeUrl}</p>
                    <button type="button" onClick={() => copyToClipboard(intakeUrl, 'Link de API')} className="mt-2 inline-flex items-center gap-1 text-blue-600"><FaLink /> <FaCopy /> Copiar link</button>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <p className="mb-1 font-semibold text-slate-500">Token de autenticação</p>
                    <p className="break-all">{source.token || 'Sem token disponível'}</p>
                    {source.token ? <button type="button" onClick={() => copyToClipboard(source.token, 'Token')} className="mt-2 inline-flex items-center gap-1 text-blue-600"><FaKey /> <FaCopy /> Copiar token</button> : null}
                  </div>
                </div>
              </article>
            );
          })}
          {!sources.length ? <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-500">Nenhuma fonte criada ainda. Cadastre uma fonte para gerar o endpoint de integração.</div> : null}
        </div>
      )}
    </section>
  );
}

export default function AutomationHub({ openTab }) {
  const [automationView, setAutomationView] = useState('process');
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get('/processes/integrations/summary');
        if (!isMounted) return;
        setSummary(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!isMounted) return;
        const message = err?.response?.data?.error || 'Não foi possível carregar as automações.';
        setError(message);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  const metrics = useMemo(() => {
    const totalProcesses = summary.length;
    const withAutomation = summary.filter((item) => item.totalIntegrations > 0).length;
    const adoptionRate = totalProcesses
      ? Math.round((withAutomation / totalProcesses) * 100)
      : 0;
    const totalActive = summary.reduce(
      (acc, item) => acc + Number(item.activeIntegrations || 0),
      0,
    );
    const pending = summary.reduce(
      (acc, item) => acc + Number(item.inactiveIntegrations || 0),
      0,
    );
    return {
      totalProcesses,
      withAutomation,
      adoptionRate,
      totalActive,
      pending,
    };
  }, [summary]);

  const filteredProcesses = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return summary.filter((process) => {
      const matchesSearch = !normalizedSearch
        ? true
        : [process.processName, process.category]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(normalizedSearch));

      const hasAutomations = process.totalIntegrations > 0;
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'with' && hasAutomations) ||
        (statusFilter === 'without' && !hasAutomations);

      return matchesSearch && matchesStatus;
    });
  }, [summary, searchTerm, statusFilter]);

  const openSettings = (processItem, templateDef = null) => {
    if (!openTab) return;
    const title = processItem.processName
      ? `Integrações: ${processItem.processName}`
      : `Integrações: Processo #${processItem.processId}`;
    const tabProps = {
      processId: processItem.processId,
      processName: processItem.processName,
    };

    if (templateDef) {
      const baseName = processItem.processName
        ? processItem.processName
        : `Processo ${processItem.processId}`;
      const nameSuffix = templateDef.template?.nameSuffix || templateDef.shortLabel;
      const payload = {
        name: `${baseName} - ${nameSuffix}`,
        targetUrl:
          typeof templateDef.template?.targetUrl === 'string'
            ? templateDef.template.targetUrl
            : '',
        method: templateDef.template?.method || 'POST',
        events: Array.isArray(templateDef.template?.events)
          ? templateDef.template.events
          : ['PROCESS_COMPLETED'],
        headers: templateDef.template?.headers || null,
        timeoutMs: templateDef.template?.timeoutMs || 15000,
        active: true,
      };
      tabProps.template = payload;
    }

    openTab(
      `process-${processItem.processId}-integrations`,
      title,
      ProcessIntegrationSettings,
      tabProps,
    );
  };

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-slate-200/70 bg-blue-600/20 p-6 shadow-xl">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl space-y-3">
            <h1 className="flex items-center gap-2 text-3xl font-semibold text-slate-900">
              Central de Automação
              <InfoTooltip label="Conecte processos do BPM a robôs RPA e integrações externas em poucos cliques." />
            </h1>
            <div className="flex flex-wrap gap-2 text-xs font-semibold text-slate-700">
              {automationHighlights.map((item) => (
                <span
                  key={item.id}
                  className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 shadow-sm"
                  title={item.description}
                >
                  <FaCheckCircle className="text-emerald-500" />
                  {item.label}
                </span>
              ))}
            </div>
          </div>
          <div className="grid w-full max-w-md grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-md">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
                  <FaCogs />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Processos mapeados</p>
                  <p className="text-xl font-semibold text-slate-900">
                    {metrics.totalProcesses.toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-md">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                  <FaPlug />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Integrações ativas</p>
                  <p className="text-xl font-semibold text-slate-900">
                    {metrics.totalActive.toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-md sm:col-span-2">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-100 text-purple-600">
                  <FaCheckCircle />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Adoção de automações</p>
                  <p className="text-xl font-semibold text-slate-900">{metrics.adoptionRate}%</p>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>{metrics.withAutomation} de {metrics.totalProcesses}</span>
              <InfoTooltip label="Total de processos com automações configuradas." />
            </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200/70 bg-white/90 p-3 shadow-sm">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setAutomationView('process')}
            className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === 'process'
                ? 'bg-blue-600 text-white shadow'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Hub de Integrações de Processos
          </button>
          <button
            type="button"
            onClick={() => setAutomationView('user-provisioning')}
            className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === 'user-provisioning'
                ? 'bg-blue-600 text-white shadow'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Automação de Cadastro de Usuário
          </button>
          <button
            type="button"
            onClick={() => setAutomationView('inventory-provisioning')}
            className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === 'inventory-provisioning'
                ? 'bg-blue-600 text-white shadow'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Integração de Estoque
          </button>
          <button
            type="button"
            onClick={() => setAutomationView('asset-provisioning')}
            className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
              automationView === 'asset-provisioning'
                ? 'bg-blue-600 text-white shadow'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Integração de Patrimônio
          </button>
        </div>
      </section>

      {automationView === 'user-provisioning' ? <UserProvisioningAutomationPanel /> : null}
      {automationView === 'inventory-provisioning' ? (
        <ResourceProvisioningPanel
          title="Integração de Estoque"
          description="Crie fontes de integração para cadastrar itens de estoque via API externa."
          icon={FaBoxes}
          sourceBasePath="/inventory/provisioning"
          defaultPath="/inventory/provisioning/intake"
          payloadExample={`{\n  "items": [\n    {\n      "name": "Cabo de Rede Cat6",\n      "sku": "CB-CAT6-10M",\n      "warehouseId": 1,\n      "categoryId": 1,\n      "stock": 150\n    }\n  ]\n}`}
        />
      ) : null}
      {automationView === 'asset-provisioning' ? (
        <ResourceProvisioningPanel
          title="Integração de Patrimônio"
          description="Crie fontes de integração para importar ativos patrimoniais de sistemas externos."
          icon={FaBuilding}
          sourceBasePath="/assets/provisioning"
          defaultPath="/assets/provisioning/intake"
          payloadExample={`{\n  "assets": [\n    {\n      "name": "Notebook Dell Latitude",\n      "tag": "PAT-2044",\n      "serialNumber": "DL-99887",\n      "status": "OPERATING",\n      "categoryName": "TI",\n      "locationName": "Sede"\n    }\n  ]\n}`}
        />
      ) : null}

      {automationView === 'process' ? (
        <>
      <section className="space-y-4 rounded-3xl border border-slate-200/70 bg-white/95 p-6 shadow-xl">
        <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-slate-900">Processos e automações</h2>
            <InfoTooltip label="Busque um processo e ajuste integrações rapidamente." />
          </div>
          <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
            <div className="relative md:w-72">
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar por nome ou categoria"
                className="w-full rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                aria-label="Buscar processos"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value="all">Todos os processos</option>
              <option value="with">Somente com automações</option>
              <option value="without">Somente sem automações</option>
            </select>
          </div>
        </header>

        {loading ? (
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
            <FaSync className="animate-spin" /> Carregando panorama de automações...
          </div>
        ) : error ? (
          <div className="flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <FaExclamationTriangle /> {error}
          </div>
        ) : filteredProcesses.length ? (
          <div className="grid gap-4 xl:grid-cols-2">
            {filteredProcesses.map((process) => {
              const hasAutomations = process.totalIntegrations > 0;
              const Icon = hasAutomations ? FaCheckCircle : FaPlug;
              return (
                <article
                  key={process.processId}
                  className="flex h-full flex-col justify-between rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-md transition hover:-translate-y-0.5 hover:shadow-xl"
                >
                  <div className="space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">
                          {process.processName || `Processo #${process.processId}`}
                        </h3>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                            <Icon className={hasAutomations ? 'text-emerald-500' : 'text-slate-400'} />
                            {hasAutomations ? 'Automação configurada' : 'Sem automação'}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                            Versão {process.version}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                            {process.status === 'published' ? 'Publicado' : 'Rascunho'}
                          </span>
                          {process.category ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                              {process.category}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-center text-xs text-slate-500">
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-3">
                        <p className="text-xs uppercase tracking-wider text-emerald-700">Ativas</p>
                        <p className="mt-1 text-lg font-semibold text-emerald-700">
                          {process.activeIntegrations}
                        </p>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <p className="text-xs uppercase tracking-wider text-slate-600">Total</p>
                        <p className="mt-1 text-lg font-semibold text-slate-700">
                          {process.totalIntegrations}
                        </p>
                      </div>
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                        <p className="text-xs uppercase tracking-wider text-amber-700">Pendentes</p>
                        <p className="mt-1 text-lg font-semibold text-amber-700">
                          {process.inactiveIntegrations}
                        </p>
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                      Última atualização das integrações: {formatDateTime(process.lastIntegrationUpdate)}
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    <button
                      type="button"
                      onClick={() => openSettings(process)}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-md transition hover:bg-blue-700"
                    >
                      Abrir configurações <FaArrowRight className="text-xs" />
                    </button>
                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Templates recomendados
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {automationTemplates.map((templateDef) => {
                          const TemplateIcon = templateDef.icon;
                          return (
                            <button
                              key={`${process.processId}-${templateDef.id}`}
                              type="button"
                              onClick={() => openSettings(process, templateDef)}
                              className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium shadow-sm transition hover:shadow ${templateDef.accent}`}
                            >
                              <TemplateIcon /> {templateDef.shortLabel}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 p-6 text-center text-sm text-slate-500">
            Nenhum processo encontrado com os filtros selecionados.
          </div>
        )}
      </section>

      <section className="space-y-4 rounded-3xl border border-slate-200/70 bg-white/95 p-6 shadow-xl">
        <header className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-slate-900">Templates de automação</h2>
          <InfoTooltip label="Cada template já sugere método, headers e eventos principais." />
        </header>
        <div className="grid gap-4 md:grid-cols-3">
          {automationTemplates.map((templateDef) => {
            const TemplateIcon = templateDef.icon;
            return (
              <div
                key={templateDef.id}
                className="flex h-full flex-col justify-between rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-md"
              >
                <div className="space-y-3">
                  <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${templateDef.accent}`}>
                    <TemplateIcon /> {templateDef.shortLabel}
                  </div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-slate-900">{templateDef.title}</h3>
                    <InfoTooltip label={templateDef.description} />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                  <span>Detalhes</span>
                  <InfoTooltip
                    label={`Método ${templateDef.template.method} · Timeout ${templateDef.template.timeoutMs / 1000}s · Eventos ${templateDef.template.events.join(', ')}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="space-y-4 rounded-3xl border border-slate-200/70 bg-white/95 p-6 shadow-xl">
        <header className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-slate-900">Boas práticas rápidas</h2>
          <InfoTooltip label="Passe o cursor nas dicas para ver instruções objetivas." />
        </header>
        <div className="flex flex-wrap gap-2">
          {automationTips.map((tip) => (
            <span
              key={tip.id}
              className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-600"
              title={tip.description}
            >
              {tip.label}
            </span>
          ))}
        </div>
      </section>
        </>
      ) : null}
    </div>
  );
}
